/**
 * Bodegas: que bodegas de SAP publican stock hacia Janis (F3).
 *
 * Ninguna bodega publica sola (decision del 2026-08-27): se descubre en SAP,
 * se crea en Janis y recien entonces alguien la habilita, quedando registrado
 * quien fue. Los tres pasos son de operacion y se muestran solo con el permiso
 * `warehouses:write`; la API vuelve a exigir el rol, esconder el boton es
 * cortesia, no seguridad.
 */
import { useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { isNeverPublishedWarehouse } from '../api/contract-schemas';
import type { WarehouseLocationOverrides, WarehouseMapping } from '../api/contracts';
import { useMonitorApi } from '../app/api-context';
import { useCan } from '../auth/auth-context';
import { ErrorBanner } from '../components/ErrorBanner';
import { EmptyState, LoadingBlock } from '../components/Feedback';
import { SelectFilter } from '../components/SelectFilter';
import {
  CreateInJanisDialog,
  DisableWarehouseDialog,
  LinkReferenceDialog,
} from '../components/WarehouseDialogs';
import { useAsyncResource } from '../hooks/use-async-resource';
import type { Option } from '../lib/catalogs';
import { formatDateTime } from '../lib/format';

type StateFilter = 'enabled' | 'disabled' | 'unmapped';

const STATE_OPTIONS: readonly Option<StateFilter>[] = [
  { value: 'enabled', label: 'Habilitadas' },
  { value: 'disabled', label: 'Deshabilitadas' },
  { value: 'unmapped', label: 'Sin crear en Janis' },
];

type DialogState =
  | { readonly kind: 'create'; readonly warehouse: WarehouseMapping }
  | { readonly kind: 'link'; readonly warehouse: WarehouseMapping }
  | { readonly kind: 'disable'; readonly warehouse: WarehouseMapping };

function matchesState(warehouse: WarehouseMapping, state: StateFilter | null): boolean {
  switch (state) {
    case null:
      return true;
    case 'enabled':
      return warehouse.enabled;
    case 'disabled':
      return !warehouse.enabled;
    case 'unmapped':
      return warehouse.janisWarehouseId === null;
  }
}

function matchesSearch(warehouse: WarehouseMapping, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === '') {
    return true;
  }
  return [warehouse.sapWarehouseCode, warehouse.label ?? '', warehouse.janisWarehouseId ?? '']
    .join(' ')
    .toLowerCase()
    .includes(needle);
}

export function WarehousesPage(): ReactElement {
  const api = useMonitorApi();
  const canWrite = useCan('warehouses:write');

  const [search, setSearch] = useState('');
  const [state, setState] = useState<StateFilter | null>(null);
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [dialog, setDialog] = useState<DialogState | null>(null);

  const resource = useAsyncResource(() => api.listWarehouses(), [api]);
  const all = useMemo(() => resource.data ?? [], [resource.data]);
  const rows = useMemo(
    () => all.filter((w) => matchesState(w, state) && matchesSearch(w, search)),
    [all, state, search],
  );
  const enabledCount = all.filter((w) => w.enabled).length;

  const start = (code: string | null): void => {
    setBusyCode(code);
    setActionError(null);
    setNotice(null);
    setWarning(null);
  };

  const fail = (error: unknown): void => {
    setBusyCode(null);
    setDiscovering(false);
    setActionError(error);
  };

  const finish = (message: string): void => {
    setBusyCode(null);
    setDialog(null);
    setNotice(message);
    resource.reload();
  };

  const discover = (): void => {
    start(null);
    setDiscovering(true);
    void api.discoverWarehouses().then((outcome) => {
      setDiscovering(false);
      const inactive =
        outcome.inactiveInSap === 0 ? '' : ` ${String(outcome.inactiveInSap)} inactivas en SAP.`;
      finish(
        `SAP devolvió ${String(outcome.read)} bodegas: ${String(outcome.created)} nuevas ` +
          `(deshabilitadas) y ${String(outcome.updated)} ya registradas.${inactive}`,
      );
    }, fail);
  };

  const save = (
    warehouse: WarehouseMapping,
    changes: Partial<WarehouseMapping>,
    message: string,
  ) => {
    start(warehouse.sapWarehouseCode);
    void api
      .saveWarehouse({
        sapWarehouseCode: warehouse.sapWarehouseCode,
        janisWarehouseId: changes.janisWarehouseId ?? warehouse.janisWarehouseId,
        label: warehouse.label,
        enabled: changes.enabled ?? warehouse.enabled,
      })
      .then(() => finish(message), fail);
  };

  const createInJanis = (warehouse: WarehouseMapping, location: WarehouseLocationOverrides) => {
    start(warehouse.sapWarehouseCode);
    void api.createWarehouseInJanis(warehouse.sapWarehouseCode, location).then((outcome) => {
      finish(
        `Se creó ${outcome.sapWarehouseCode} en Janis con la referencia ` +
          `${outcome.janisWarehouseId}. Sigue deshabilitada.`,
      );
      if (!outcome.warehouseConfirmed) {
        setWarning(
          `Janis creó la ubicación de ${outcome.sapWarehouseCode}, pero al releerla no apareció ` +
            'su almacén. No la habilites hasta revisarla en Janis: el stock sería rechazado.',
        );
      }
    }, fail);
  };

  return (
    <div className="page">
      <header className="page__header">
        <div>
          <h1>Bodegas</h1>
          <p className="page__subtitle">
            Qué bodegas de SAP publican stock en Janis. Ninguna publica sola: se descubre en SAP, se
            crea en Janis y después se habilita. El cambio se aplica en la próxima corrida de stock.
          </p>
        </div>
        <div className="row">
          <button type="button" className="button" onClick={resource.reload}>
            Actualizar
          </button>
          {canWrite ? (
            <button
              type="button"
              className="button button--primary"
              disabled={discovering}
              onClick={discover}
            >
              {discovering ? 'Leyendo SAP…' : 'Descubrir bodegas en SAP'}
            </button>
          ) : null}
        </div>
      </header>

      <section className="panel">
        <div className="panel__body">
          <div className="filters">
            <label className="field">
              <span className="field__label">Buscar</span>
              <input
                className="input"
                type="search"
                value={search}
                placeholder="Código o nombre"
                onChange={(event) => setSearch(event.target.value)}
              />
            </label>
            <SelectFilter
              label="Estado"
              value={state}
              options={STATE_OPTIONS}
              allLabel="Todas"
              onChange={setState}
            />
          </div>
        </div>
      </section>

      {notice === null ? null : (
        <div className="banner banner--info" role="status">
          {notice}
        </div>
      )}
      {warning === null ? null : (
        <div className="banner banner--warning" role="alert">
          {warning}
        </div>
      )}
      {dialog === null ? <ErrorBanner error={actionError} /> : null}
      <ErrorBanner error={resource.error} onRetry={resource.reload} />

      <section className="panel">
        <header className="panel__header">
          <h2>
            {resource.data === null
              ? 'Bodegas registradas'
              : `${String(all.length)} registradas · ${String(enabledCount)} habilitadas`}
          </h2>
        </header>

        {resource.loading && resource.data === null ? (
          <LoadingBlock />
        ) : rows.length === 0 ? (
          all.length === 0 ? (
            <EmptyState
              title="No hay bodegas registradas en este ambiente."
              hint="Descubrirlas en SAP las registra todas deshabilitadas."
            />
          ) : (
            <EmptyState title="Ninguna bodega calza con estos filtros." />
          )
        ) : (
          <div className="table__wrap">
            <table className="table table--stack">
              <thead>
                <tr>
                  <th scope="col">Código SAP</th>
                  <th scope="col">Nombre</th>
                  <th scope="col">Referencia en Janis</th>
                  <th scope="col">Estado</th>
                  <th scope="col">Último cambio</th>
                  {canWrite ? <th scope="col">Acciones</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((warehouse) => {
                  const code = warehouse.sapWarehouseCode;
                  const busy = busyCode === code;
                  const mapped = warehouse.janisWarehouseId !== null;
                  const neverPublishes = isNeverPublishedWarehouse(code);
                  return (
                    <tr key={code}>
                      <td className="mono table__lead nowrap">{code}</td>
                      <td data-label="Nombre">{warehouse.label ?? '—'}</td>
                      <td className="nowrap" data-label="Referencia en Janis">
                        {mapped ? (
                          <span className="mono">{warehouse.janisWarehouseId}</span>
                        ) : (
                          <span className="muted">Sin crear en Janis</span>
                        )}
                      </td>
                      <td className="nowrap table__pill">
                        {warehouse.enabled ? (
                          <span className="badge badge--ok">Habilitada</span>
                        ) : (
                          <span className="badge">Deshabilitada</span>
                        )}
                        {neverPublishes ? (
                          <span
                            className="badge badge--error"
                            title="Guarda mercadería ya vendida (tránsito o «por retirar»): si se publicara, Janis la volvería a vender."
                          >
                            Nunca publica
                          </span>
                        ) : null}
                      </td>
                      <td className="nowrap" data-label="Último cambio">
                        {warehouse.updatedAt === null ? (
                          '—'
                        ) : (
                          <>
                            {formatDateTime(warehouse.updatedAt)}
                            {warehouse.updatedBy === null ? null : (
                              <span className="muted"> · {warehouse.updatedBy}</span>
                            )}
                          </>
                        )}
                      </td>
                      {canWrite ? (
                        <td>
                          <div className="table__actions">
                            {mapped ? null : (
                              <button
                                type="button"
                                className="button button--sm button--primary"
                                disabled={busy}
                                aria-label={`Crear ${code} en Janis`}
                                onClick={() => {
                                  setActionError(null);
                                  setDialog({ kind: 'create', warehouse });
                                }}
                              >
                                Crear en Janis
                              </button>
                            )}
                            {warehouse.enabled ? null : (
                              <button
                                type="button"
                                className="button button--sm button--ghost"
                                disabled={busy}
                                aria-label={`Vincular ${code} con Janis`}
                                onClick={() => {
                                  setActionError(null);
                                  setDialog({ kind: 'link', warehouse });
                                }}
                              >
                                {mapped ? 'Cambiar referencia' : 'Vincular'}
                              </button>
                            )}
                            {mapped && !warehouse.enabled && !neverPublishes ? (
                              <button
                                type="button"
                                className="button button--sm button--primary"
                                disabled={busy}
                                aria-label={`Habilitar ${code}`}
                                onClick={() =>
                                  save(
                                    warehouse,
                                    { enabled: true },
                                    `${code} quedó habilitada: publica stock desde la próxima corrida.`,
                                  )
                                }
                              >
                                Habilitar
                              </button>
                            ) : null}
                            {warehouse.enabled ? (
                              <button
                                type="button"
                                className="button button--sm button--danger"
                                disabled={busy}
                                aria-label={`Deshabilitar ${code}`}
                                onClick={() => {
                                  setActionError(null);
                                  setDialog({ kind: 'disable', warehouse });
                                }}
                              >
                                Deshabilitar
                              </button>
                            ) : null}
                          </div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {dialog?.kind === 'create' ? (
        <CreateInJanisDialog
          warehouse={dialog.warehouse}
          busy={busyCode === dialog.warehouse.sapWarehouseCode}
          error={actionError}
          onCancel={() => setDialog(null)}
          onConfirm={(location) => createInJanis(dialog.warehouse, location)}
        />
      ) : null}
      {dialog?.kind === 'link' ? (
        <LinkReferenceDialog
          warehouse={dialog.warehouse}
          busy={busyCode === dialog.warehouse.sapWarehouseCode}
          error={actionError}
          onCancel={() => setDialog(null)}
          onConfirm={(janisWarehouseId) =>
            save(
              dialog.warehouse,
              { janisWarehouseId },
              `${dialog.warehouse.sapWarehouseCode} quedó vinculada a ${janisWarehouseId}. Sigue deshabilitada.`,
            )
          }
        />
      ) : null}
      {dialog?.kind === 'disable' ? (
        <DisableWarehouseDialog
          warehouse={dialog.warehouse}
          busy={busyCode === dialog.warehouse.sapWarehouseCode}
          error={actionError}
          onCancel={() => setDialog(null)}
          onConfirm={() =>
            save(
              dialog.warehouse,
              { enabled: false },
              `${dialog.warehouse.sapWarehouseCode} quedó deshabilitada: deja de publicar stock.`,
            )
          }
        />
      ) : null}
    </div>
  );
}
